import "core-js/modules/es.array.is-array.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.symbol.iterator.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.from.js";
import "core-js/modules/es.regexp.exec.js";
function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormSymbolContext } from "./Form.context";
import { createSetValues } from "./Form.hooks";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
export function createFormProviderComponent(_ref) {
  var FormContext = _ref.FormContext;
  return function FormProvider(props) {
    var contextValue = useInitForm(props);
    return _jsx(FormContext.Provider, {
      value: contextValue,
      children: props.children
    });
  };
}
export function createFormComponent(_ref2) {
  var formSymbol = _ref2.formSymbol,
    provider = _ref2.provider,
    FormContext = _ref2.FormContext;
  var useFormContext = function useFormContext(props) {
    if (provider) {
      // It's okay to call this conditionally in this case, because provider is
      // a set value that never changes.  This means the order the hooks are
      // called will be the same every render
      // eslint-disable-next-line react-hooks/rules-of-hooks
      var context = useContext(FormContext);
      if (!context) {
        throw new Error('When provider=true, then the Form must be wrapped in FormProvider');
      }
      return context;
    }
    if (!('defaultValues' in props)) {
      throw new Error('defaultValues is required');
    }

    // It's okay to call this conditionally in this case, because provider is
    // a set value that never changes.  This means the order the hooks are
    // called will be the same every render
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useInitForm(props);
  };
  function Form(props) {
    var id = props.id,
      ariaLabel = props['aria-label'],
      ariaLabelledBy = props['aria-labelledby'],
      children = props.children;
    var formRef = useRef(null);
    var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      error = _useState2[0],
      setError = _useState2[1];
    useEffect(function () {
      var _formRef$current, _formRef$current$pare;
      if ((_formRef$current = formRef.current) !== null && _formRef$current !== void 0 && (_formRef$current$pare = _formRef$current.parentElement) !== null && _formRef$current$pare !== void 0 && _formRef$current$pare.closest('form')) {
        setError('Nesting forms is not supported.  HTML5 does not allow nesting <form> within another <form> and doing so would violate [WCAG 4.1.1](https://www.w3.org/WAI/WCAG21/Understanding/parsing.html)');
      }
    }, []);
    if (error) {
      throw new Error(error);
    }
    var formContext = useFormContext(props);
    var setValues = createSetValues(formContext.context.setValue);
    if (ariaLabel && ariaLabelledBy) {
      throw new Error('Only one of aria-label or aria-labelledby can be provided');
    }
    var formEl = _jsx("form", {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      id: id,
      noValidate: true,
      onSubmit: function onSubmit(e) {
        // Submit events in react are propagated through the logical react
        // tree, not the DOM tree.  This means if you have a form inside a
        // dialog that is rendered inside another form, the submit event
        // will be fired on both forms.  We don't really want this odd
        // behavior, so we stop the event propagation here.
        //
        // See:
        // https://react.dev/reference/react-dom/createPortal#caveats
        e.stopPropagation();
        formContext.context.handleSubmit(function (values) {
          return formContext.onSubmitRef.current(values, {
            setValues: setValues
          });
        }, formContext.onErrorRef.current)(e);
      },
      ref: formRef,
      children: children
    });
    if (provider) {
      return _jsx(FormSymbolContext.Provider, {
        value: formSymbol,
        children: formEl
      });
    }
    return _jsx(FormSymbolContext.Provider, {
      value: formSymbol,
      children: _jsx(FormContext.Provider, {
        value: formContext,
        children: formEl
      })
    });
  }
  return Form;
}

/**
 * Initializes the form and returns the form utils / functions
 */
function useInitForm(props) {
  var context = useForm({
    /**
     * Unlike React Hook Form, we want to enforce a default value to be specified for every single field
     * in order to prevent runtime errors on the other part of our form abstraction. (e.g. `onSubmit`)
     * To learn more about the rationale, see https://github.com/ApplyBoard/platform_monorepo_ui/pull/4241#discussion_r981789989
     */
    defaultValues: props.defaultValues,
    mode: props.validationMode || 'onBlur'
  });
  var onSubmitRef = useRef(props.onSubmit);
  onSubmitRef.current = props.onSubmit;
  var onErrorRef = useRef(props.onError);
  onErrorRef.current = props.onError;
  var defaultValuesRef = useRef(props.defaultValues);
  defaultValuesRef.current = props.defaultValues;

  // For performance reasons, we should keep this referentially stable, even if
  // the props change.  That ensures that sub-components don't re-render just
  // because FormProvider or Form re-rendered
  return useMemo(function () {
    return {
      context: context,
      onSubmitRef: onSubmitRef,
      onErrorRef: onErrorRef,
      defaultValuesRef: defaultValuesRef
    };
  }, [context]);
}